/**
 * Rétention & alertes réseau (SuperAdmin, brique F) — LOGIQUE PURE.
 *
 * Compare l'activité de chaque resto (période courante vs précédente) pour repérer
 * les baisses, et expose la base fidèle / dormante du réseau. Aucune I/O ici
 * (cf. retentionService.ts). Données via RPC `network_retention`.
 */

/** Seuil d'alerte « en baisse » : -20 % de commandes vs période précédente. */
export const DECLINE_THRESHOLD = 0.2;

export type RetentionStatus = 'en_hausse' | 'stable' | 'en_baisse' | 'nouveau' | 'inactif';

export const RETENTION_LABELS: Record<RetentionStatus, string> = {
  en_hausse: 'En hausse',
  stable: 'Stable',
  en_baisse: 'En baisse',
  nouveau: 'Démarrage',
  inactif: 'Inactif',
};

export interface TenantRetention {
  tenantId: string;
  name: string;
  slug: string;
  ordersCurrent: number;
  ordersPrevious: number;
  /** Variation relative vs période précédente : >0 = baisse, <0 = hausse. */
  dropRatio: number;
  status: RetentionStatus;
}

export interface NetworkRetention {
  windowDays: number;
  tenants: TenantRetention[];
  dormantCustomers: number;
  loyalCustomers: number;
}

/**
 * Calcule la baisse relative (cur vs prev) et le statut.
 *   prev>0 : dropRatio = (prev-cur)/prev  → 0.5 = -50 %
 *   prev=0, cur>0 : démarrage (pas de base de comparaison)
 *   prev=0, cur=0 : inactif
 */
export function computeDrop(ordersCurrent: number, ordersPrevious: number): {
  dropRatio: number;
  status: RetentionStatus;
} {
  if (ordersPrevious === 0) {
    if (ordersCurrent === 0) return { dropRatio: 0, status: 'inactif' };
    return { dropRatio: -1, status: 'nouveau' };
  }
  const dropRatio = (ordersPrevious - ordersCurrent) / ordersPrevious;
  let status: RetentionStatus;
  if (dropRatio > DECLINE_THRESHOLD) status = 'en_baisse';
  else if (dropRatio < -DECLINE_THRESHOLD) status = 'en_hausse';
  else status = 'stable';
  return { dropRatio, status };
}

interface RawRetention {
  tenant_id?: string;
  name?: string | null;
  slug?: string | null;
  orders_current?: number | null;
  orders_previous?: number | null;
}

function toTenantRetention(raw: RawRetention): TenantRetention {
  const ordersCurrent = raw.orders_current ?? 0;
  const ordersPrevious = raw.orders_previous ?? 0;
  const { dropRatio, status } = computeDrop(ordersCurrent, ordersPrevious);
  return {
    tenantId: raw.tenant_id ?? '',
    name: raw.name ?? '',
    slug: raw.slug ?? '',
    ordersCurrent,
    ordersPrevious,
    dropRatio,
    status,
  };
}

/** Parse la réponse jsonb de la RPC. */
export function parseRetention(data: unknown): NetworkRetention {
  const obj = (data ?? {}) as {
    window_days?: number;
    tenants?: RawRetention[];
    dormant_customers?: number;
    loyal_customers?: number;
  };
  return {
    windowDays: obj.window_days ?? 30,
    tenants: Array.isArray(obj.tenants) ? obj.tenants.map(toTenantRetention) : [],
    dormantCustomers: obj.dormant_customers ?? 0,
    loyalCustomers: obj.loyal_customers ?? 0,
  };
}

/** Restos en baisse (statut en_baisse), triés du plus fort recul au plus faible. */
export function decliningTenants(tenants: TenantRetention[]): TenantRetention[] {
  return tenants.filter((t) => t.status === 'en_baisse').sort((a, b) => b.dropRatio - a.dropRatio);
}

/** Tri d'affichage : baisses d'abord (plus fort recul en tête). */
export function sortByDrop(tenants: TenantRetention[]): TenantRetention[] {
  return [...tenants].sort((a, b) => b.dropRatio - a.dropRatio);
}

/** Variation signée affichable (ex. dropRatio 0.3 → "-30 %", -0.2 → "+20 %"). */
export function changeLabel(t: TenantRetention): string {
  if (t.status === 'nouveau') return 'nouveau';
  if (t.status === 'inactif') return '—';
  const pct = Math.round(t.dropRatio * 100);
  if (pct === 0) return '0 %';
  return pct > 0 ? `-${pct} %` : `+${-pct} %`;
}

export interface RetentionKpis {
  declining: number;
  loyal: number;
  dormant: number;
  tenants: number;
  rising: number;
}

/** KPIs d'en-tête (tous dérivés des données de la RPC — aucune valeur figée). */
export function retentionKpis(ret: NetworkRetention | null): RetentionKpis {
  const tenants = ret?.tenants ?? [];
  return {
    declining: tenants.filter((t) => t.status === 'en_baisse').length,
    rising: tenants.filter((t) => t.status === 'en_hausse').length,
    loyal: ret?.loyalCustomers ?? 0,
    dormant: ret?.dormantCustomers ?? 0,
    tenants: tenants.length,
  };
}

/** Message d'alerte synthétique (ou null si rien à signaler). */
export function retentionAlert(tenants: TenantRetention[]): { count: number; names: string[]; message: string } | null {
  const declining = decliningTenants(tenants);
  if (declining.length === 0) return null;
  const names = declining.map((t) => t.name);
  return {
    count: declining.length,
    names,
    message: `${declining.length} resto(s) en baisse à relancer : ${names.join(', ')}.`,
  };
}
